import React, { useState } from 'react';
import { Phone, MapPin, Mail, Clock } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Toaster } from './ui/sonner';
import { toast } from 'sonner';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    vehicle: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Mock form submission
    setTimeout(() => {
      console.log('Form submitted:', formData);
      toast.success('Request submitted successfully! We\'ll contact you soon.');
      setFormData({
        name: '',
        phone: '',
        email: '',
        vehicle: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <section id="contact" className="relative py-24 bg-neutral-950">
      <Toaster position="top-right" />
      {/* Carbon Fiber Background */}
      <div className="absolute inset-0 carbon-fiber opacity-10"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="text-red-600 text-sm font-bold tracking-widest uppercase">Contact Us</span>
            <div className="h-0.5 bg-red-600 mt-2 mx-auto"></div>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Get In Touch
          </h2>
          <p className="text-xl text-white/70 max-w-3xl mx-auto">
            Have questions or need to schedule service? We're here to help.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-white mb-6">K&B Auto Services Inc.</h3>
              
              <div className="space-y-6">
                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-600 flex items-center justify-center flex-shrink-0" style={{ borderRadius: '0px' }}>
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-white/60 text-sm mb-1">Phone</div>
                    <a href="tel:7742425144" className="text-white text-lg font-medium hover:text-red-600 transition-colors">
                      (774) 242-5144
                    </a>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-600 flex items-center justify-center flex-shrink-0" style={{ borderRadius: '0px' }}>
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-white/60 text-sm mb-1">Address</div>
                    <p className="text-white text-lg">
                      522 SW Cutoff<br />
                      Worcester, MA 01607
                    </p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-600 flex items-center justify-center flex-shrink-0" style={{ borderRadius: '0px' }}>
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-white/60 text-sm mb-1">Email</div>
                    <a href="mailto:kbautorepair715@gmail.com" className="text-white text-lg font-medium hover:text-red-600 transition-colors break-all">
                      kbautorepair715@gmail.com
                    </a>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-600 flex items-center justify-center flex-shrink-0" style={{ borderRadius: '0px' }}>
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-white/60 text-sm mb-1">Hours</div>
                    <p className="text-white text-lg">
                      Monday - Friday: 8am - 5pm<br />
                      Saturday: By Appointment<br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="mt-8">
              <div className="w-full h-[300px] bg-neutral-900 border border-white/10" style={{ borderRadius: '0px' }}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2954.123!2d-71.8023!3d42.21169!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s522%20SW%20Cutoff%2C%20Worcester%2C%20MA%2001607!5e0!3m2!1sen!2sus!4v1630000000000"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="K&B Auto Services Location"
                ></iframe>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-neutral-900 border border-white/10 p-8" style={{ borderRadius: '0px' }}>
            <h3 className="text-2xl font-bold text-white mb-6">Request Service</h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-white text-sm font-medium mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-black border border-white/20 text-white px-4 py-3 focus:outline-none focus:border-red-600 transition-colors"
                  style={{ borderRadius: '0px' }}
                  placeholder="Your name"
                />
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-white text-sm font-medium mb-2">
                  Phone *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full bg-black border border-white/20 text-white px-4 py-3 focus:outline-none focus:border-red-600 transition-colors"
                  style={{ borderRadius: '0px' }}
                  placeholder="(774) 242-5144"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-white text-sm font-medium mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-black border border-white/20 text-white px-4 py-3 focus:outline-none focus:border-red-600 transition-colors"
                  style={{ borderRadius: '0px' }}
                  placeholder="your@email.com"
                />
              </div>

              {/* Vehicle */}
              <div>
                <label htmlFor="vehicle" className="block text-white text-sm font-medium mb-2">
                  Vehicle Year/Make/Model
                </label>
                <input
                  type="text"
                  id="vehicle"
                  name="vehicle"
                  value={formData.vehicle}
                  onChange={handleChange}
                  className="w-full bg-black border border-white/20 text-white px-4 py-3 focus:outline-none focus:border-red-600 transition-colors"
                  style={{ borderRadius: '0px' }}
                  placeholder="e.g., 2020 Honda Accord"
                />
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-white text-sm font-medium mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="4"
                  className="w-full bg-black border border-white/20 text-white px-4 py-3 focus:outline-none focus:border-red-600 transition-colors resize-none"
                  style={{ borderRadius: '0px' }}
                  placeholder="Tell us about your service needs..."
                ></textarea>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 text-white px-8 py-4 text-lg font-medium transition-all duration-300 hover:scale-105 disabled:hover:scale-100"
                style={{ borderRadius: '0px', boxShadow: '0 0 20px rgba(255, 0, 0, 0.3)' }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;